import { Injectable, NgZone, inject } from '@angular/core';
import { environment } from '@ao/environments/environment';
import { MsalService } from '@azure/msal-angular';
import { BehaviorSubject, fromEvent, merge, timer, Subscription } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { LogoutService } from './logout.service';

@Injectable({
  providedIn: 'root',
})
export class IdleService {
  private msalService = inject(MsalService);
  private ngZone = inject(NgZone);
  private logoutService = inject(LogoutService);

  private idleTimeout$ = new BehaviorSubject<boolean>(false);
  private idleTime = environment.idleConfig.idleTimeMinutes * 60 * 1000;
  private timeoutWarningTime =
    environment.idleConfig.timeoutWarningTimeMinutes * 60 * 1000;
  private activitySubscription: Subscription | null = null;
  private timerSubscription: Subscription | null = null;
  private isPopupDisplayed = false;

  constructor() {
    this.startWatching();
  }

  get idleTimeout() {
    return this.idleTimeout$.asObservable();
  }

  private startWatching() {
    const activityEvents = merge(
      fromEvent(window, 'mousemove'),
      fromEvent(window, 'keydown'),
      fromEvent(window, 'click')
    );

    this.activitySubscription = activityEvents
      .pipe(
        tap(() => this.resetTimeout()),
        switchMap(() => timer(this.idleTime))
      )
      .subscribe(() => this.ngZone.run(() => this.showTimeoutWarning()));
  }

  private resetTimeout() {
    if (this.isPopupDisplayed) {
      return;
    }
    this.idleTimeout$.next(false);
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
    }
    this.timerSubscription = timer(this.idleTime).subscribe(() =>
      this.ngZone.run(() => this.showTimeoutWarning())
    );
  }

  private showTimeoutWarning() {
    this.isPopupDisplayed = true;
    this.idleTimeout$.next(true);
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
    }

    this.timerSubscription = timer(this.timeoutWarningTime).subscribe(() =>
      this.ngZone.run(() => this.logoutService.logout())
    );
  }

  continueSession() {
    this.isPopupDisplayed = false;
    this.msalService.instance
      .acquireTokenSilent({
        scopes: environment.apiConfig.apiScope,
      })
      .then(() => {
        this.resetTimeout();
      })
      .catch(() => {
        this.logoutService.logout();
      });
  }
}
